/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {NgEventBus} from 'ng-event-bus';
import {SpatialUtilService} from '../../../../services/spatial-util.service';
import {LookupService} from '../../../../../common/service/lookup.service';
import {LineTypeLookup} from '../../../../../common/models/line-type.model';
import {MapToolsService} from '../../../../services/map-tools.service';
import {PlanModelService} from '../../../../../common/service/plan-model.service';
import {LineService} from '../../../../services/features/line.service';
import {EsriModuleService} from '../../../../esri-nlr/services/esri-module.service';
import {switchMap} from 'rxjs/operators';
import {first, of, tap} from 'rxjs';
import {ConstructionToolModelService} from './construction-tool-model.service';
import {FeaturesApplyInsertService} from '../../../../services/features-apply-insert.service';
import {PointTypeLookup} from '../../../../../common/models/point-type.model';
import {ConstructTypeLookup} from '../../../../../common/models/construct-type.model';
import {SpatialSequenceService} from '../../../../../common/service/spatial-sequence.service';
import {ToolServiceInterface} from './tool-service-interface';

@Injectable({
    providedIn: 'root'
})
export class BoundaryOffsetConstructionToolService implements ToolServiceInterface {
    title = 'BoundaryOffsetConstructionToolService';

    attributes: any;

    constructor(
        private constructionToolModelService: ConstructionToolModelService,
        private featuresApplyInsertService: FeaturesApplyInsertService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private lineService: LineService,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private mapToolsService: MapToolsService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialUtilService: SpatialUtilService
    ) {}

    create(attributes: any) {
        console.debug(`${this.title} : create`);
        return this.mapToolsService.activate('CONSTRUCTION_TOOLS_BOUNDARY_OFFSET', () => {
            this.attributes = attributes;

            const selectedLines = this.spatialUtilService.getHighlightedLines();
            const selectedPoints = [...this.spatialUtilService.getHighlightedPoints(), ...this.spatialUtilService.getHighlightedControlPoints()];

            if (this.validateSelection(selectedLines, selectedPoints)) {
                const subscription = this.createBoundaryOffset(attributes, selectedLines[0], selectedPoints[0]).subscribe();
                this.eventBus.cast('nlr:busy', subscription);
            }
        });
    }

    getPointAlongLine(bearing: number, distance: number) {
        return [
            (distance * Math.sin((bearing * Math.PI) / 180) * Math.pow(10, 6)) / Math.pow(10, 6),
            (distance * Math.cos((bearing * Math.PI) / 180) * Math.pow(10, 6)) / Math.pow(10, 6)
        ];
    }

    getConstructTypeCornerLookup(): ConstructTypeLookup {
        return this.lookupService.getLookups('constructType').find((l: ConstructTypeLookup) => l.description === 'Corner Offset');
    }

    getPointTypeLookup(): PointTypeLookup {
        return this.lookupService.getLookups('pointType').find((l: PointTypeLookup) => l.businessClass === 'ConstructPoint');
    }

    getLineTypeLookup(): LineTypeLookup {
        return this.lookupService.getLookups('lineType').find((l: LineTypeLookup) => l.businessClass === 'ConstructLine');
    }

    private getBoundaryOffsetConstructTypeLookup(): ConstructTypeLookup {
        return this.lookupService.getLookups('constructType').find((l: ConstructTypeLookup) => l.description === 'Boundary Offset');
    }

    private getTool() {
        return this.constructionToolModelService.getTool('boundaryOffset');
    }

    private validateSelection(selectedLineFeatures: any[], selectedPointFeatures: any[]) {
        const tool = this.getTool();

        tool.errors.length = 0;
        tool.warnings.length = 0;
        tool.information.length = 0;

        if (selectedLineFeatures.length === 1 && selectedPointFeatures.length === 1) {
            const pointId = parseInt(selectedPointFeatures[0].attributes.id);

            if (
                parseInt(selectedLineFeatures[0].attributes.frompointid) === pointId ||
                parseInt(selectedLineFeatures[0].attributes.topointid) == pointId
            ) {
                return true;
            } else {
                tool.errors.push(tool.errorMessage2);
                return false;
            }
        } else {
            tool.errors.push(tool.errorMessage1);
            return false;
        }
    }

    private createPointFeatures(points: any[]) {
        console.debug(`${this.title} - createPointFeatures`);

        return this.featuresApplyInsertService
            .insertFeaturesByType(this.spatialUtilService.getConstructPointMapLayer(), points)
            .pipe(tap(() => console.debug('Points have been successfully created!')));
    }

    private createLineFeatures(lines: any[]) {
        console.debug(`${this.title} - createLinesFeatures`);

        return this.featuresApplyInsertService
            .insertFeaturesByType(this.spatialUtilService.getConstructLineMapLayer(), lines)
            .pipe(tap(() => console.debug('Lines have been successfully created!')));
    }

    private createBoundaryOffset(attributes: any, selectedLine: any, selectedPoint: any) {
        const pointSequenceId = this.spatialSequenceService.getConstructPointSequence(1)[0];
        const lineSequenceId = this.spatialSequenceService.getConstructLineSequence(1)[0];

        const fromPointId = selectedLine.attributes.frompointid;
        const toPointId = selectedLine.attributes.topointid;

        let fromPoint: any;
        let toPoint: any;

        if (parseInt(fromPointId) === parseInt(selectedPoint.attributes.id)) {
            fromPoint = selectedPoint;
            toPoint = this.spatialUtilService.getPointGraphics().find((f: any) => f.attributes.id === toPointId);
        } else if (parseInt(toPointId) === parseInt(selectedPoint.attributes.id)) {
            fromPoint = selectedPoint;
            toPoint = this.spatialUtilService.getPointGraphics().find((f: any) => f.attributes.id === fromPointId);
        } else {
            return of(null);
        }

        let bearing = this.lineService.calculateBearing(fromPoint.geometry.x, fromPoint.geometry.y, toPoint.geometry.x, toPoint.geometry.y);
        const distance = attributes['offsetdistance'];

        if (attributes['markplacement'] === 'NO') {
            bearing = 180 + bearing;
        }

        let distanceNotToScale = distance;

        if (attributes['nottoscaleoffsetdistance']) {
            distanceNotToScale = attributes['nottoscaleoffsetdistance'];
        }

        const pointAlongLine = this.getPointAlongLine(bearing, distanceNotToScale);

        const pointGeometry = new this.esriModuleService.geometry.Point(
            fromPoint.geometry.x + pointAlongLine[0],
            fromPoint.geometry.y + pointAlongLine[1]
        );
        pointGeometry.spatialReference = new this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference());

        const boundaryOffsetConstructTypeLookup = this.getBoundaryOffsetConstructTypeLookup();
        const pointTypeLookup = this.getPointTypeLookup();
        const lineTypeLookup = this.getLineTypeLookup();

        const pointAttributes = {
            type: pointTypeLookup.id,
            constructtype: boundaryOffsetConstructTypeLookup.id,
            sourcefromlineid: selectedLine.attributes.id,
            guid: this.planModelService.getGuid(),
            constructfromlineid: lineSequenceId,
            sourcelineids: `[${lineSequenceId}]`,
            id: pointSequenceId
        };

        for (const [key, value] of Object.entries(attributes) as any[]) {
            if (value && typeof value === 'object') {
                pointAttributes[key] = value.id;
            } else {
                pointAttributes[key] = value;
            }
        }

        const pointGraphic = new this.esriModuleService.Graphic(pointGeometry);
        pointGraphic.attributes = pointAttributes;
        const pointsToBeCreated = [];
        pointsToBeCreated.push(pointGraphic);

        return this.createPointFeatures(pointsToBeCreated).pipe(
            switchMap((createdPoints: any[]) => {
                console.debug(`${this.title} - created point count: ${createdPoints.length}`);

                const lineGeometry = new this.esriModuleService.geometry.Polyline([
                    [fromPoint.geometry.x, fromPoint.geometry.y],
                    [pointGeometry.x, pointGeometry.y]
                ]);
                lineGeometry.spatialReference = new this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference());

                console.debug(`${this.title} - Point 1: ${lineGeometry.paths[0][0][0]} / ${lineGeometry.paths[0][0][1]}`);
                console.debug(`${this.title} - Point 2: ${lineGeometry.paths[0][1][0]} / ${lineGeometry.paths[0][1][1]}`);

                const lineAttributes: any = {
                    frompointid: `[${fromPoint.attributes.id}]`,
                    bearing: bearing,
                    distance: distance,
                    type: lineTypeLookup.id,
                    constructtype: boundaryOffsetConstructTypeLookup.id,
                    lineid: selectedLine.attributes.id,
                    distancenottoscale: distanceNotToScale,
                    id: lineSequenceId,
                    guid: this.planModelService.getGuid()
                };

                if (createdPoints?.length > 0 && createdPoints[0].attributes) {
                    lineAttributes.topointid = '(' + createdPoints[0].attributes.id + ')';
                }

                const lineGraphic = new this.esriModuleService.Graphic(lineGeometry);
                lineGraphic.attributes = lineAttributes;

                const linesToBeCreated = [];
                linesToBeCreated.push(lineGraphic);

                return this.createLineFeatures(linesToBeCreated).pipe(
                    tap((createdLines: any[]) => console.debug(`${this.title} - created line count: ${createdLines.length}`))
                );
            }),
            first()
        );
    }
}
